import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Pagination,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
  Modal,
} from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';

import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LayersIcon from '@mui/icons-material/Layers';

import {
  AesAnalyzerAPI,
  AesFilterOptions,
  AesLine,
  AesRunDetails,
  AesRunStatus,
  aesFindingSignature,
} from 'api/AesAnalyzerAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import ToolBreadcrumb from 'components/Common/ToolBreadcrumb';
import DeleteConfirmationModal from 'components/Analyzers/DeleteConfirmationModal';
import AesSeverityChip, {
  AES_SEVERITY_LABEL,
} from 'components/Analyzers/AesSeverityChip';
import AesFilterModal, {
  AesFilters,
  EMPTY_AES_FILTERS,
  countAesFilters,
} from 'components/Analyzers/AesFilterModal';
import AesBulkClearModal from 'components/Analyzers/AesBulkClearModal';
import { usmlDisplay } from 'components/Analyzers/aesFormat';
import ColumnPicker from 'components/Analyzers/columns/ColumnPicker';
import { useSavedColumns } from 'components/Analyzers/columns/useSavedColumns';
import {
  AES_COLUMNS,
  AES_TABLE_KEY,
  unavailableAesColumns,
} from 'components/Analyzers/columns/aesColumns';
import { aesAnalyzerReportPDF } from 'components/Common/EntityTicketPdf';
import 'resources/styles/aes-analyzer.css';

const PAGE_SIZE = 50;

const TABS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All Lines' },
  { key: 'flagged', label: 'Flagged' },
  { key: 'clean', label: 'Clean' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'cleared', label: 'Cleared' },
];

const usd = (v: unknown, compact = false) =>
  v === null || v === undefined || v === ''
    ? '--'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}),
      }).format(Number(v));

const statusClass = (status?: string) =>
  status === 'Completed'
    ? 'completed'
    : status === 'Needs Review'
    ? 'needs-review'
    : status === 'Failed'
    ? 'failed'
    : 'running';

const AesRunDetailsContent: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const history = useHistory();
  const { cur_user } = useContext(GlobalContext);
  const groupId: string | undefined = cur_user?.selected_team?.id;

  const [run, setRun] = useState<AesRunDetails | null>(null);
  const [lines, setLines] = useState<AesLine[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingLines, setLoadingLines] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState<AesFilters>(EMPTY_AES_FILTERS);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState<AesFilterOptions>({
    severities: [],
    rules: [],
    countries: [],
    licenseTypes: [],
    routed: [],
  });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [isBulkWorking, setIsBulkWorking] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'row_number', direction: 'asc' });
  const { visible: visibleColumnKeys, setVisible: setVisibleColumnKeys } =
    useSavedColumns(AES_TABLE_KEY, AES_COLUMNS);

  const [selectedLine, setSelectedLine] = useState<AesLine | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showRawColumns, setShowRawColumns] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [workingSignature, setWorkingSignature] = useState<string | null>(null);

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReRunning, setIsReRunning] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const isProcessing = run?.status === 'Running' || run?.status === 'Pending';

  // Each fetch takes a sequence number and only the latest one may write
  // state. Filter/sort changes fetch once with the old page and again after
  // the page resets to 1, and a team switch or poll can overlap too; without
  // this, whichever response arrived last would win, not the newest request.
  const runRequestId = useRef(0);
  const linesRequestId = useRef(0);
  const optionsRequestId = useRef(0);

  const fetchRun = useCallback(async () => {
    if (!groupId || !runId) return;
    const requestId = ++runRequestId.current;
    try {
      const data = await AesAnalyzerAPI.getRun(runId, groupId);
      if (requestId !== runRequestId.current) return;
      setRun(data);
      setNotFound(false);
    } catch (e: any) {
      if (requestId !== runRequestId.current) return;
      if (e?.response?.status === 404) setNotFound(true);
      else console.error('Failed to load AES run', e);
    }
  }, [groupId, runId]);

  const fetchLines = useCallback(async () => {
    if (!groupId || !runId) return;
    const requestId = ++linesRequestId.current;
    setLoadingLines(true);
    try {
      const data = await AesAnalyzerAPI.getLines(runId, groupId, {
        page: currentPage,
        pageSize: PAGE_SIZE,
        tab: activeTab,
        search: debouncedSearch || undefined,
        severities: filters.severities,
        rules: filters.rules,
        countries: filters.countries,
        licenseTypes: filters.licenseTypes,
        routed: filters.routed,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
      });
      if (requestId !== linesRequestId.current) return;
      // An edit can empty the last page (e.g. clearing its only flagged
      // line); step back to the new last page instead of showing nothing.
      if (!data.lines.length && data.total > 0 && currentPage > 1) {
        setCurrentPage(Math.ceil(data.total / PAGE_SIZE));
        return;
      }
      setLines(data.lines);
      setTotal(data.total);
    } catch (e) {
      if (requestId !== linesRequestId.current) return;
      console.error('Failed to load AES lines', e);
    } finally {
      if (requestId === linesRequestId.current) setLoadingLines(false);
    }
  }, [
    groupId,
    runId,
    currentPage,
    activeTab,
    debouncedSearch,
    filters,
    sortConfig,
  ]);

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  useEffect(() => {
    fetchLines();
  }, [fetchLines]);

  // Poll while the audit is running, then load the finished lines once.
  useEffect(() => {
    if (!isProcessing) return undefined;
    const id = setInterval(fetchRun, 3000);
    return () => clearInterval(id);
  }, [isProcessing, fetchRun]);

  const [wasProcessing, setWasProcessing] = useState(false);
  useEffect(() => {
    if (isProcessing) setWasProcessing(true);
    else if (wasProcessing) {
      setWasProcessing(false);
      fetchLines();
    }
  }, [isProcessing]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedSearch, filters, sortConfig]);

  // Filter options change only when the run finishes (or is re-run).
  useEffect(() => {
    if (!groupId || !runId || !run || isProcessing) return;
    const requestId = ++optionsRequestId.current;
    AesAnalyzerAPI.getFilterOptions(runId, groupId)
      .then((options) => {
        if (requestId === optionsRequestId.current) setFilterOptions(options);
      })
      .catch((e) => console.error('Failed to load AES filter options', e));
  }, [groupId, runId, run?.status, run?.updated_at]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const ruleById = useMemo(
    () => Object.fromEntries((run?.rules || []).map((r) => [r.id, r])),
    [run?.rules],
  );

  const handleSort = (key: string) =>
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));

  /** Replace a line everywhere it is shown after a mutation. */
  const applyLineUpdate = (updated: AesLine) => {
    setLines((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setSelectedLine((prev) => (prev?.id === updated.id ? updated : prev));
  };

  const handleStatusChange = async (status: AesRunStatus) => {
    setStatusMenuAnchor(null);
    if (!groupId || !run || status === run.status) return;
    try {
      await AesAnalyzerAPI.updateStatus(run.id, groupId, status);
      setRun((prev) => (prev ? { ...prev, status: status } : prev));
      enqueueSnackbar(`Status set to ${status}`, { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Failed to update status',
        { variant: 'error' },
      );
    }
  };

  const handleReRun = async () => {
    if (!groupId || !run) return;
    setIsReRunning(true);
    try {
      const queued = await AesAnalyzerAPI.reRun(run.id, groupId);
      setRun((prev) =>
        prev ? { ...prev, status: queued?.status || 'Pending' } : prev,
      );
      enqueueSnackbar(
        'Re-running against the current rules. Reviews, notes and cleared findings are kept.',
        { variant: 'info' },
      );
    } catch (e: any) {
      // A 409 explains why (the run's lines were never fully saved).
      enqueueSnackbar(e?.response?.data?.message || 'Failed to start re-run', {
        variant: 'error',
      });
    } finally {
      setIsReRunning(false);
    }
  };

  const handleDelete = async () => {
    if (!groupId || !run) return;
    setIsDeleting(true);
    try {
      await AesAnalyzerAPI.deleteRun(run.id, groupId);
      enqueueSnackbar('Analysis deleted', { variant: 'success' });
      history.push('/aes-analyzers');
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message || 'Failed to delete analysis',
        { variant: 'error' },
      );
      setIsDeleting(false);
    }
  };

  const handleDownload = async () => {
    if (!groupId || !run) return;
    setIsDownloading(true);
    try {
      await AesAnalyzerAPI.downloadFindingsCsv(run.id, groupId, run.name);
    } catch (e) {
      enqueueSnackbar('Failed to download findings', { variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  // The lines in state are whatever page/filter/sort the user currently has
  // open; the PDF is a full-run audit, so it fetches every line itself,
  // page by page (the server caps pageSize at 200), ignoring the active
  // tab/filter/search so it always covers the whole run.
  const fetchAllLinesForReport = async (): Promise<AesLine[]> => {
    if (!groupId || !run) return [];
    const all: AesLine[] = [];
    let page = 1;
    for (;;) {
      const data = await AesAnalyzerAPI.getLines(run.id, groupId, {
        page: page,
        pageSize: 200,
        tab: 'all',
        sortKey: 'row_number',
        sortDir: 'asc',
      });
      all.push(...data.lines);
      if (all.length >= data.total || !data.lines.length) break;
      page += 1;
    }
    return all;
  };

  const handleDownloadPdf = async () => {
    if (!groupId || !run) return;
    setIsDownloadingPdf(true);
    try {
      const allLines = await fetchAllLinesForReport();
      await aesAnalyzerReportPDF(
        {
          run: run,
          lines: allLines,
          preparedBy:
            [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
              .filter(Boolean)
              .join(' ') || undefined,
        },
        `BITE AES Report - ${run.name} - ${moment().format('MM-DD-YYYY')}.pdf`,
      );
    } catch (e) {
      enqueueSnackbar('Failed to download the PDF report', {
        variant: 'error',
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleToggleFinding = async (signature: string, clear: boolean) => {
    if (!groupId || !selectedLine) return;
    setWorkingSignature(signature);
    try {
      const updated = await AesAnalyzerAPI.setFindingCleared(
        selectedLine.run_id,
        selectedLine.id,
        groupId,
        signature,
        clear,
      );
      applyLineUpdate(updated);
      // Clearing or restoring can move the line in or out of the current
      // tab/filters (e.g. its last finding cleared while viewing Flagged).
      fetchRun();
      fetchLines();
    } catch (e) {
      enqueueSnackbar(`Failed to ${clear ? 'clear' : 'restore'} finding`, {
        variant: 'error',
      });
    } finally {
      setWorkingSignature(null);
    }
  };

  const handleBulk = async (rule: string, clear: boolean) => {
    if (!groupId || !run) return;
    setIsBulkWorking(true);
    try {
      const result = await AesAnalyzerAPI.setRuleFindingsCleared(
        run.id,
        groupId,
        rule,
        clear,
      );
      const flag = ruleById[rule]?.flag || 'finding';
      const n = result.findings;
      enqueueSnackbar(
        n
          ? `${clear ? 'Cleared' : 'Restored'} ${n.toLocaleString()} "${flag}" finding${
              n === 1 ? '' : 's'
            } on ${result.lines.toLocaleString()} line${
              result.lines === 1 ? '' : 's'
            }.`
          : `No "${flag}" findings to ${clear ? 'clear' : 'restore'}.`,
        { variant: n ? 'success' : 'info' },
      );
      setShowBulkModal(false);
      // The open drawer's line may have changed too; drop it rather than
      // show stale clears.
      setIsDrawerOpen(false);
      fetchRun();
      fetchLines();
    } catch (e: any) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          `Failed to ${clear ? 'clear' : 'restore'} findings`,
        { variant: 'error' },
      );
    } finally {
      setIsBulkWorking(false);
    }
  };

  const handleToggleReviewed = async () => {
    if (!groupId || !selectedLine) return;
    try {
      const updated = await AesAnalyzerAPI.setReviewed(
        selectedLine.run_id,
        selectedLine.id,
        groupId,
        !selectedLine.is_reviewed,
      );
      applyLineUpdate(updated);
      // Toggling reviewed changes membership of the Reviewed tab.
      fetchRun();
      fetchLines();
    } catch (e) {
      enqueueSnackbar('Failed to update review status', { variant: 'error' });
    }
  };

  const handleAddNote = async () => {
    // Enter reaches this too, and the note text stays until the save returns,
    // so a second Enter would otherwise post the same note again.
    if (!groupId || !selectedLine || !newNote.trim() || isSubmittingNote) {
      return;
    }
    setIsSubmittingNote(true);
    try {
      const updated = await AesAnalyzerAPI.addNote(
        selectedLine.run_id,
        selectedLine.id,
        groupId,
        newNote.trim(),
      );
      applyLineUpdate(updated);
      setNewNote('');
    } catch (e) {
      enqueueSnackbar('Failed to add note', { variant: 'error' });
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const selectedIndex = selectedLine
    ? lines.findIndex((l) => l.id === selectedLine.id)
    : -1;
  const openLine = (line: AesLine) => {
    setSelectedLine(line);
    setShowRawColumns(false);
    setNewNote('');
    setIsDrawerOpen(true);
  };

  const sortHeader = (
    label: string,
    key: string,
    style?: React.CSSProperties,
  ) => (
    <th
      onClick={() => handleSort(key)}
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {label}
        {sortConfig.key === key &&
          (sortConfig.direction === 'asc' ? (
            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
          ))}
      </div>
    </th>
  );

  // Every finding on the line with its state: open, cleared by a user, or
  // auto-cleared by the analyzer (shown with its reason, never as open).
  const activeFindingsOf = (line: AesLine) => {
    const cleared = new Set((line.cleared_flags || []).map((c) => c.signature));
    return (line.findings || []).map((f) => ({
      finding: f,
      signature: aesFindingSignature(f),
      cleared: !!f.autoCleared || cleared.has(aesFindingSignature(f)),
    }));
  };
  const hasRoutedColumn = !!run?.column_map?.routed;

  // A saved column whose field this run's upload lacks stays saved (other runs
  // may have it) but isn't drawn here.
  const unavailableColumns = unavailableAesColumns(run?.column_map);
  const shownColumns = AES_COLUMNS.filter(
    (c) => visibleColumnKeys.includes(c.key) && !unavailableColumns[c.key],
  );
  // The chosen columns plus the fixed status marker, Row and Findings.
  const tableColSpan = shownColumns.length + 3;

  const handleColumnsChange = (next: typeof visibleColumnKeys) => {
    const sortedColumnHidden = AES_COLUMNS.some(
      (c) => c.sortKey === sortConfig.key && !next.includes(c.key),
    );
    if (sortedColumnHidden) setSortConfig({ key: 'row_number', direction: 'asc' });
    setVisibleColumnKeys(next);
  };

  if (notFound) {
    return (
      <div className='analyzers-dashboard-root'>
        <div style={{ marginBottom: '-20px' }}>
          <ToolBreadcrumb
            items={[
              { label: 'AES Analyzer', to: '/aes-analyzers' },
              { label: 'Not found' },
            ]}
          />
        </div>
        <div className='aes-panel' style={{ margin: '0 24px' }}>
          This analysis does not exist or belongs to another team.
        </div>
      </div>
    );
  }

  // Checks that could not fully run on this file — kept visible (in the
  // notes box) so a clean-looking line is never mistaken for a checked one.
  const coverageGaps = (run?.coverage || []).filter(
    (c) => c.status !== 'evaluated',
  );
  const activeFilterCount = countAesFilters(filters);
  const showAbout =
    !!run &&
    !isProcessing &&
    (!!run.upload_notes?.length ||
      !!run.audit_notes?.length ||
      coverageGaps.length > 0);

  return (
    <div className='analyzers-dashboard-root'>
      <div style={{ marginBottom: '-20px' }}>
        <ToolBreadcrumb
          items={[
            { label: 'AES Analyzer', to: '/aes-analyzers' },
            { label: run?.name || 'Loading...' },
          ]}
        />
      </div>

      {/* Header */}
      <div className='detail-header-flex'>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className='analyzers-page-title' style={{ marginBottom: 0 }}>
              {run?.name || 'Loading...'}
            </h1>
            <span className={`run-badge ${statusClass(run?.status)}`}>
              {run?.status || '--'}
            </span>
            {(run?.status === 'Completed' ||
              run?.status === 'Needs Review') && (
              <>
                <Tooltip title='Edit status'>
                  <button
                    onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                    aria-label='Edit status'
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      padding: '2px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                  </button>
                </Tooltip>
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={!!statusMenuAnchor}
                  onClose={() => setStatusMenuAnchor(null)}
                >
                  {(['Completed', 'Needs Review'] as AesRunStatus[]).map(
                    (s) => (
                      <MenuItem
                        key={s}
                        selected={run?.status === s}
                        onClick={() => handleStatusChange(s)}
                      >
                        {s}
                      </MenuItem>
                    ),
                  )}
                </Menu>
              </>
            )}
          </div>
          <div className='run-detail-header-meta'>
            AES •{' '}
            {run?.created_at
              ? moment(run.created_at).format('MM/DD/YYYY')
              : '--'}{' '}
            • {Number(run?.findings_count || 0).toLocaleString()} findings
            {run?.initiated_by_name && <> • {run.initiated_by_name}</>}
            {run?.original_file_name && <> • {run.original_file_name}</>}
          </div>
        </div>
        <div className='detail-header-actions' style={{ flexWrap: 'wrap' }}>
          {/* Consolidates the CSV/PDF downloads under one menu, matching the
              ACE Analyzer run page's "Create Report" pattern instead of two
              separate standalone buttons. */}
          <button
            className='btn-outline'
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            disabled={isProcessing || !run}
            style={{
              opacity: isProcessing ? 0.6 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            {isDownloading || isDownloadingPdf ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
            )}{' '}
            Download Findings
          </button>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={!!downloadMenuAnchor}
            onClose={() => setDownloadMenuAnchor(null)}
            PaperProps={{
              sx: {
                mt: 1,
                bgcolor: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'var(--bg-hover)',
                },
              },
            }}
          >
            <MenuItem
              disabled={isDownloadingPdf}
              onClick={() => {
                setDownloadMenuAnchor(null);
                handleDownloadPdf();
              }}
            >
              <PictureAsPdfOutlinedIcon
                style={{ fontSize: 18, marginRight: 8, color: '#4895ef' }}
              />
              Download Findings (PDF)
            </MenuItem>
            <MenuItem
              disabled={isDownloading}
              onClick={() => {
                setDownloadMenuAnchor(null);
                handleDownload();
              }}
            >
              <FileDownloadOutlinedIcon
                style={{ fontSize: 18, marginRight: 8, color: '#10b981' }}
              />
              Download Findings (CSV)
            </MenuItem>
          </Menu>
          <Tooltip title='Re-audit these lines against the current rules and reference data'>
            <span>
              <button
                className='btn-primary'
                onClick={handleReRun}
                disabled={isProcessing || isReRunning || !run}
                style={{
                  opacity: isProcessing || isReRunning ? 0.6 : 1,
                  cursor:
                    isProcessing || isReRunning ? 'not-allowed' : 'pointer',
                }}
              >
                <ReplayIcon style={{ fontSize: 18 }} />{' '}
                {isProcessing ? 'Processing...' : 'Re-Run'}
              </button>
            </span>
          </Tooltip>
          <button
            className='btn-outline'
            onClick={() => setDeleteModalOpen(true)}
            disabled={isProcessing || !run}
            aria-label='Delete analysis'
            style={{
              borderColor: 'var(--low-color)',
              color: 'var(--low-color)',
              opacity: isProcessing ? 0.6 : 1,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      {showAbout && run && (
        <div
          className='aes-panel aes-upload-notes'
          style={{ margin: '0 24px' }}
        >
          <InfoOutlinedIcon
            style={{ fontSize: 18, color: 'var(--action-color)' }}
          />
          <div style={{ minWidth: 0 }}>
            <div className='aes-panel-title' style={{ fontSize: 14 }}>
              About this file
            </div>
            <ul>
              {[...(run.upload_notes || []), ...(run.audit_notes || [])].map(
                (n, i) => (
                  <li key={i}>{n}</li>
                ),
              )}
              {coverageGaps.length > 0 && (
                <li>
                  {coverageGaps.length} of {run.coverage.length} checks could
                  not fully run on this file.{' '}
                  <button
                    type='button'
                    className='aes-link-btn'
                    onClick={() => setShowCoverage((v) => !v)}
                  >
                    {showCoverage ? 'Hide details' : 'Show details'}
                  </button>
                  {showCoverage && (
                    <ul className='aes-coverage-gaps'>
                      {coverageGaps.map((c) => (
                        <li key={c.rule}>
                          <strong>{ruleById[c.rule]?.flag || 'Check'}</strong>{' '}
                          ({c.status === 'partial' ? 'partial' : 'not run'})
                          {c.reason ? ` — ${c.reason}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {run?.status === 'Failed' && (
        <div
          className='aes-panel'
          style={{
            margin: '0 24px',
            borderColor: 'rgba(220, 38, 38, 0.3)',
            color: 'var(--low-color)',
          }}
        >
          This analysis failed.{' '}
          {run.error_message ||
            'Re-run it, or upload the file again.'}
        </div>
      )}

      {/* KPIs */}
      <div className='detail-kpi-grid'>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <DescriptionIcon style={{ fontSize: 16, color: '#4895ef' }} />
            <span className='kpi-card-label'>Commodity Lines</span>
          </div>
          <div className='kpi-card-value text-blue'>
            {Number(run?.line_count || 0).toLocaleString()}
          </div>
          {!!run?.shipment_count && (
            <div className='card-db-meta-span'>
              across {Number(run.shipment_count).toLocaleString()} shipments
              {typeof run.credits_charged === 'number' &&
                ` · ${Number(run.credits_charged).toLocaleString()} credit${
                  Number(run.credits_charged) === 1 ? '' : 's'
                } used`}
            </div>
          )}
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <WarningAmberIcon style={{ fontSize: 16, color: '#f79009' }} />
            <span className='kpi-card-label'>Flagged Lines</span>
          </div>
          <div className='kpi-card-value text-orange'>
            {isProcessing
              ? '--'
              : Number(run?.flagged_line_count || 0).toLocaleString()}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <ReportGmailerrorredOutlinedIcon
              style={{ fontSize: 16, color: 'var(--low-color)' }}
            />
            <span className='kpi-card-label'>Critical Lines</span>
          </div>
          <div className='kpi-card-value' style={{ color: 'var(--low-color)' }}>
            {isProcessing
              ? '--'
              : Number(run?.critical_count || 0).toLocaleString()}
          </div>
        </div>
        <div className='detail-kpi-card'>
          <div className='kpi-card-header'>
            <AttachMoneyIcon style={{ fontSize: 16, color: '#20c997' }} />
            <span className='kpi-card-label'>Total Value</span>
          </div>
          <div className='kpi-card-value text-teal'>
            {usd(run?.total_value || 0, true)}
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className='analyzers-section' style={{ marginTop: '10px' }}>
        <div className='analyzers-section-header'>
          <h2 className='section-title'>Uploaded Lines</h2>
          <p className='section-subtitle'>
            Each commodity line with its findings. Click a line to review it.
          </p>
        </div>

        <div className='data-toolbar-container'>
          <div className='data-search-wrapper'>
            <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
            <input
              type='text'
              className='data-search-input'
              placeholder='Search shipment, ITN, consignee, ECCN, HTS...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={isProcessing}
            />
          </div>

          <div className='data-segmented-control'>
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`data-segment-btn ${
                  activeTab === t.key ? 'active' : ''
                }`}
                onClick={() => setActiveTab(t.key)}
                disabled={isProcessing}
                style={{
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                  opacity: isProcessing ? 0.6 : 1,
                }}
              >
                {t.label}{' '}
                <span className='data-segment-count'>
                  {(
                    run?.tabCounts?.[
                      t.key as keyof AesRunDetails['tabCounts']
                    ] || 0
                  ).toLocaleString()}
                </span>
              </button>
            ))}
          </div>

          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <button
              className='data-segment-btn'
              onClick={() => setShowFilterModal(true)}
              disabled={isProcessing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: activeFilterCount
                  ? 'var(--action-color)'
                  : 'var(--text-secondary)',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              <FilterListIcon style={{ fontSize: 16 }} />
              Filters {activeFilterCount ? `(${activeFilterCount})` : ''}
            </button>
            {activeFilterCount > 0 && (
              <Tooltip title='Clear Filters'>
                <button
                  className='data-segment-btn'
                  onClick={() => setFilters(EMPTY_AES_FILTERS)}
                  aria-label='Clear Filters'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px',
                    color: 'var(--text-secondary)',
                  }}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            )}
            <button
              className='data-segment-btn'
              onClick={() => setShowBulkModal(true)}
              disabled={isProcessing || !run}
              title='Clear or restore every finding of one kind on this run'
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--text-secondary)',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                opacity: isProcessing ? 0.6 : 1,
              }}
            >
              <LayersIcon style={{ fontSize: 16 }} />
              Bulk Actions
            </button>
            <ColumnPicker
              columns={AES_COLUMNS}
              visible={visibleColumnKeys}
              onChange={handleColumnsChange}
              unavailable={unavailableColumns}
              disabled={isProcessing || !run}
            />
          </div>
        </div>

        <div className='runs-table-container acp-scroll-x'>
          <table className='run-data-table'>
            <thead>
              <tr>
                <th style={{ width: '32px' }}></th>
                {sortHeader('Row', 'row_number', { width: '64px' })}
                {shownColumns.map((column) =>
                  column.sortKey ? (
                    <React.Fragment key={column.key}>
                      {sortHeader(column.header, column.sortKey, column.headerStyle)}
                    </React.Fragment>
                  ) : (
                    <th key={column.key} style={column.headerStyle}>
                      {column.header}
                    </th>
                  ),
                )}
                {sortHeader('Findings', 'severity')}
              </tr>
            </thead>
            <tbody>
              {(isProcessing || (loadingLines && lines.length === 0)) &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td colSpan={tableColSpan}>
                      <div
                        style={{
                          height: 16,
                          borderRadius: 4,
                          background: 'var(--bg-surface)',
                        }}
                      />
                    </td>
                  </tr>
                ))}
              {isProcessing && (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{
                      textAlign: 'center',
                      padding: '16px',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    Auditing lines…
                  </td>
                </tr>
              )}
              {!isProcessing &&
                lines.map((line) => {
                  const items = activeFindingsOf(line);
                  const isSelected = selectedLine?.id === line.id;
                  return (
                    <tr
                      key={line.id}
                      onClick={() => openLine(line)}
                      style={{
                        cursor: 'pointer',
                        borderLeft: `3px solid ${
                          isSelected ? 'var(--action-color)' : 'transparent'
                        }`,
                        backgroundColor: isSelected
                          ? 'var(--es-row-hover)'
                          : undefined,
                      }}
                    >
                      <td
                        style={{ textAlign: 'center', verticalAlign: 'middle' }}
                      >
                        {line.is_reviewed ? (
                          <CheckCircleOutlineIcon
                            style={{
                              fontSize: 16,
                              color: 'var(--full-bar-color)',
                            }}
                            titleAccess='Reviewed'
                          />
                        ) : (
                          <div
                            className={`aes-row-marker ${
                              line.max_severity || ''
                            }`}
                            title={
                              line.max_severity
                                ? AES_SEVERITY_LABEL[line.max_severity]
                                : 'Clean'
                            }
                          />
                        )}
                      </td>
                      <td
                        style={{
                          fontFamily: 'Inter',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {line.row_number}
                      </td>
                      {shownColumns.map((column) => (
                        <td key={column.key} style={column.cellStyle}>
                          {column.render(line, { hasRoutedColumn: hasRoutedColumn })}
                        </td>
                      ))}
                      <td style={{ padding: '8px' }}>
                        {items.length ? (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '6px',
                              maxWidth: 360,
                            }}
                          >
                            {items.map(({ finding, cleared }, i) => (
                              <AesSeverityChip
                                key={i}
                                severity={finding.severity}
                                label={finding.flag}
                                cleared={cleared}
                                title={
                                  finding.autoCleared
                                    ? `${finding.message} ${finding.autoClearReason || ''}`
                                    : finding.message
                                }
                              />
                            ))}
                          </div>
                        ) : (
                          <span
                            style={{
                              color: 'var(--text-tertiary)',
                              fontSize: 12,
                            }}
                          >
                            --
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              {!isProcessing && !loadingLines && lines.length === 0 && (
                <tr>
                  <td
                    colSpan={tableColSpan}
                    style={{ textAlign: 'center', padding: '24px' }}
                  >
                    No lines match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {!isProcessing && totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 20px',
              }}
            >
              <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                {total.toLocaleString()} lines
              </span>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_e: React.ChangeEvent<unknown>, p: number) =>
                  setCurrentPage(p)
                }
                color='primary'
              />
            </div>
          )}
        </div>
      </div>

      {/* Line drawer */}
      {selectedLine && isDrawerOpen && (
        <div className='drawer-backdrop' onClick={() => setIsDrawerOpen(false)}>
          <div className='drawer-panel' onClick={(e) => e.stopPropagation()}>
            <div className='drawer-header'>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { dir: -1, Icon: ChevronLeftIcon, title: 'Previous line' },
                  { dir: 1, Icon: ChevronRightIcon, title: 'Next line' },
                ].map(({ dir, Icon, title }) => {
                  const target = lines[selectedIndex + dir];
                  return (
                    <button
                      key={dir}
                      onClick={() => target && openLine(target)}
                      disabled={!target}
                      title={title}
                      style={{
                        background: 'transparent',
                        border: '1px solid var(--es-soft-border)',
                        color: 'inherit',
                        borderRadius: '6px',
                        padding: '4px 6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        opacity: target ? 1 : 0.4,
                        cursor: target ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <Icon style={{ fontSize: 18 }} />
                    </button>
                  );
                })}
              </div>
              <h2 className='drawer-title'>
                Row {selectedLine.row_number}
                {(selectedLine.shipment_reference || selectedLine.itn) &&
                  ` • ${selectedLine.shipment_reference || selectedLine.itn}`}
                {selectedLine.line_number &&
                  ` • Line ${selectedLine.line_number}`}
              </h2>
              <button
                className='drawer-close-btn'
                onClick={() => setIsDrawerOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>

            <div className='drawer-body'>
              <div>
                <div className='drawer-section-title'>FINDINGS</div>
                {(() => {
                  const items = activeFindingsOf(selectedLine);
                  if (!items.length) {
                    return (
                      <div
                        className='drawer-text-block'
                        style={{
                          color: 'var(--full-bar-color)',
                          fontWeight: 500,
                        }}
                      >
                        No issues detected on this line by the checks that ran.
                        See &quot;About this file&quot; above for checks that could
                        not fully run on this upload.
                      </div>
                    );
                  }
                  const clearedBy = new Map(
                    (selectedLine.cleared_flags || []).map((c) => [
                      c.signature,
                      c,
                    ]),
                  );
                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      {items.map(({ finding, signature, cleared }) => {
                        const entry = clearedBy.get(signature);
                        return (
                          <div
                            key={signature}
                            className={`aes-finding-card ${
                              cleared ? 'cleared' : ''
                            }`}
                          >
                            <div className='aes-finding-card-header'>
                              <AesSeverityChip
                                severity={finding.severity}
                                label={finding.flag}
                                cleared={cleared}
                              />
                              {/* Auto-cleared findings were closed by the
                                  analyzer with a reason; they have no
                                  Clear/Restore action. */}
                              {!finding.autoCleared && (
                                <button
                                  className={`aes-finding-btn ${
                                    cleared ? 'restore' : ''
                                  }`}
                                  disabled={workingSignature === signature}
                                  onClick={() =>
                                    handleToggleFinding(signature, !cleared)
                                  }
                                >
                                  {cleared ? 'Restore' : 'Clear'}
                                </button>
                              )}
                            </div>
                            <div className='aes-finding-message'>
                              {finding.message}
                            </div>
                            <div className='aes-finding-meta'>
                              {ruleById[finding.rule]?.title}
                              {finding.autoCleared && (
                                <>
                                  {' '}
                                  •{' '}
                                  {finding.autoClearReason ||
                                    'Auto-cleared by the analyzer.'}
                                </>
                              )}
                              {cleared && entry && (
                                <>
                                  {' '}
                                  • Cleared by {entry.clearedByName || 'a user'}
                                  {entry.clearedAt &&
                                    ` on ${moment(entry.clearedAt).format(
                                      'MM/DD/YYYY',
                                    )}`}
                                  {entry.bulk && ' (bulk clear)'}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div>
                <div className='drawer-section-title'>LINE DETAILS</div>
                <div className='drawer-kv-box'>
                  {Object.entries(run?.fieldLabels || {})
                    .filter(([field]) => run?.column_map?.[field])
                    .map(([field, label]) => (
                      <div className='drawer-kv-row' key={field}>
                        <span className='drawer-kv-label'>{label}</span>
                        <span className='drawer-kv-value'>
                          {(field === 'usml_category'
                            ? usmlDisplay(selectedLine.fields?.[field])
                            : selectedLine.fields?.[field]) || '--'}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {Object.keys(selectedLine.raw_row || {}).length > 0 && (
                <div>
                  <button
                    type='button'
                    onClick={() => setShowRawColumns((p) => !p)}
                    className='drawer-section-title'
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      cursor: 'pointer',
                    }}
                  >
                    ALL UPLOADED COLUMNS
                    {showRawColumns ? (
                      <ExpandLessOutlinedIcon style={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreOutlinedIcon style={{ fontSize: 16 }} />
                    )}
                  </button>
                  {showRawColumns && (
                    <div className='drawer-kv-box'>
                      {(run?.source_headers?.length
                        ? run.source_headers
                        : Object.keys(selectedLine.raw_row)
                      ).map((k) => (
                        <div className='drawer-kv-row' key={k}>
                          <span className='drawer-kv-label'>{k}</span>
                          <span className='drawer-kv-value'>
                            {selectedLine.raw_row[k] || '--'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div
                className='drawer-comments-section'
                style={{ marginTop: '8px', paddingTop: '16px' }}
              >
                <div className='drawer-section-title'>NOTES & COMMENTS</div>
                <div
                  className='drawer-comments-list'
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginBottom: '16px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                >
                  {!selectedLine.notes?.length ? (
                    <div
                      className='drawer-comments-empty'
                      style={{ fontSize: '13px', fontStyle: 'italic' }}
                    >
                      No notes added yet.
                    </div>
                  ) : (
                    selectedLine.notes.map((note, idx) => (
                      <div
                        key={idx}
                        className='drawer-comment-card'
                        style={{
                          padding: '10px',
                          borderRadius: '6px',
                          fontSize: '13px',
                        }}
                      >
                        <div
                          className='drawer-comment-header'
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '4px',
                          }}
                        >
                          <span
                            className='drawer-comment-author'
                            style={{ fontWeight: 600 }}
                          >
                            {note.authorName || 'Unknown user'}
                          </span>
                          <span
                            className='drawer-comment-timestamp'
                            style={{ fontSize: '11px', opacity: 0.7 }}
                          >
                            {moment(note.createdAt).format(
                              'MMM D, YYYY h:mm A',
                            )}
                          </span>
                        </div>
                        <div className='drawer-comment-text'>{note.text}</div>
                      </div>
                    ))
                  )}
                </div>
                <div
                  className='drawer-comment-input-wrapper'
                  style={{ display: 'flex', gap: '8px' }}
                >
                  <input
                    type='text'
                    className='drawer-comment-input'
                    placeholder='Add a note about this line...'
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                    }}
                  />
                  <button
                    className='drawer-comment-submit-btn'
                    onClick={handleAddNote}
                    disabled={isSubmittingNote || !newNote.trim()}
                    style={{
                      borderRadius: '6px',
                      padding: '0 12px',
                      cursor:
                        isSubmittingNote || !newNote.trim()
                          ? 'not-allowed'
                          : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <SendIcon
                      className='drawer-comment-submit-icon'
                      style={{ fontSize: 16 }}
                    />
                  </button>
                </div>
              </div>

              <div
                className='drawer-footer-actions'
                style={{ marginTop: '16px' }}
              >
                <button
                  className='drawer-btn-outline'
                  onClick={handleToggleReviewed}
                  style={
                    selectedLine.is_reviewed
                      ? undefined
                      : {
                          color: 'var(--full-bar-color)',
                          borderColor: 'var(--full-bar-color)',
                        }
                  }
                >
                  <CheckCircleOutlineIcon style={{ fontSize: 16 }} />{' '}
                  {selectedLine.is_reviewed
                    ? 'Mark as Not Reviewed'
                    : 'Mark as Reviewed'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
      >
        <AesFilterModal
          handleClose={() => setShowFilterModal(false)}
          active={filters}
          available={filterOptions}
          rules={run?.rules || []}
          onApplyFilters={(next) => {
            setFilters(next);
            setShowFilterModal(false);
          }}
          isLoading={loadingLines}
        />
      </Modal>

      <Modal
        open={showBulkModal}
        onClose={() => !isBulkWorking && setShowBulkModal(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
        }}
      >
        <AesBulkClearModal
          handleClose={() => setShowBulkModal(false)}
          rules={run?.rules || []}
          openCounts={run?.ruleCounts || {}}
          clearedCounts={run?.clearedRuleCounts || {}}
          onSubmit={handleBulk}
          isLoading={isBulkWorking}
        />
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        isDeleting={isDeleting}
        runName={run?.name}
      />
    </div>
  );
};

// Remount per team and run. A team switch updates cur_user in place, so without
// this the previous team's run, lines and open drawer stay on screen until the
// new requests land, and a still-Running run keeps polling after its 404.
const AesRunDetailsPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const { cur_user } = useContext(GlobalContext);
  return <AesRunDetailsContent key={`${cur_user?.selected_team?.id}:${runId}`} />;
};

export default AesRunDetailsPage;
